import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { RiEdit2Fill } from 'react-icons/ri';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import usePackageActions from '../../../hooks/APIHooks/finance/usePackageActions';
import { createEditPackageSchema } from '../../../schemas';
import { Package, PackageReqBody } from '../../../types/packages';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditPackageModal = ({
  title,
  creditPackage,
  handleModalClose,
}: {
  title: 'create' | 'edit';
  creditPackage?: Package;
  handleModalClose: () => void;
}) => {
  // integration of custom API hooks here
  const { createNewPackageHandler, updatePackageHandler } =
    usePackageActions('');

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle create/edit packages declared here
  const createEditPackageHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      let body: PackageReqBody = {
        name: values.package_name,
        price: values.price,
        credits: values.total_credits,
        currency: 'USD',
      };

      if (!creditPackage) {
        body = { ...body, created_by: currentUser?.username };

        await createNewPackageHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Package Created!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['package-list'] });
            } else {
              toast.error(res.message || 'Package Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body = { ...body, updated_by: currentUser?.username };

        await updatePackageHandler
          .mutateAsync({ packageId: creditPackage.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Package Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['package-list'] });
            } else {
              toast.error(res.message || 'Package Updating Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create/edit package modal here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText={`${title} package`} />

      <Formik
        initialValues={{
          package_name: creditPackage?.name || '',
          price: creditPackage?.price || '',
          total_credits: creditPackage?.credits || '',
          discount: '',
          discount_validity: '',
        }}
        validationSchema={createEditPackageSchema}
        onSubmit={createEditPackageHandler}
      >
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaInputField
              type='text'
              name='package_name'
              label='Package Name'
              placeholder='Enter package name here...'
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='price'
              label='Package Price (CAD)'
              placeholder='Enter package price in CAD here...'
              step='.01'
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='total_credits'
              label='Total Credits'
              placeholder='Enter total credits here...'
              isRequired
            />

            {/* <AcademiaInputField
              type='number'
              name='discount'
              label='Discount Percentage'
              placeholder='Enter discount percentage here...'
            />

            <AcademiaDatePicker
              name='discount_validity'
              label='Discount Validity'
              placeholder='DD-MM-YYYY'
              minDate={new Date()}
            /> */}

            <AcademiaButton
              type='submit'
              variant='premium'
              icon={
                title === 'create' ? (
                  <TbSquareRoundedPlusFilled className='size-6' />
                ) : (
                  <RiEdit2Fill className='size-6' />
                )
              }
              isLoading={props.isSubmitting}
              fullWidth
            >
              {title} Package
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateEditPackageModal;
